// AbortedSessionBanner.tsx — FE_SPEC §3.X.3.6 (M2 NEW spec) + iter_5 P2 retry CTA.
//
// Per docs/spec_v3/fe_spec/FE_SPEC.md §3.X.3.6 (DeepSeek pass-6 fix C16):
//   reason union = 'session_deadline' | `phase_fail:${string}` | 'no_choreography'
//                 | 'owner_panic' | string  (NO 'user_manual_abort')
//
// 4 substates per PR-FE-SPEC-02:
//   default: red banner + reason copy + viewReport CTA
//   loading: n/a (presentational)
//   empty:   reason==='' → return null (defensive)
//   error:   reason starts with 'phase_fail:' → parse phase + detailed copy
//
// Back-compat: legacy `abortReason` prop maps to `reason`. Legacy `sessionId`
// kept for the iter_5 retry CTA (preserves the old "回到首页 / dashboard"
// path while the new spec adds /sessions/:id/report navigation).

import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Eyebrow from '@/ui/atoms/Eyebrow';
import Button from '@/ui/atoms/Button';

// Reasons where the FE *guessed* the session was aborted (i.e. BE never said
// so explicitly — `session_lost` = both /reports and /sessions 404, and
// `session_incomplete` = legacy fallback that should now route through the
// Report.tsx `pending` page-state but is kept here as defensive sink for
// stale bundles or other call sites). For these we MUST suppress the
// "refund issued automatically" line: no refund was issued — claiming one
// is a misleading promise.
const UNCERTAIN_REASONS = new Set(['session_lost', 'session_incomplete']);

interface AbortedSessionBannerProps {
  /** ★ M2 spec — preferred prop name (FE_SPEC §3.X.3.6). */
  reason?: string | null;
  /** Legacy prop — still accepted; mapped to `reason`. */
  abortReason?: string | null;
  /** When true, render Link to /sessions/{sessionId}/report. */
  canViewReport?: boolean;
  sessionId?: string;
  className?: string;
}

export default function AbortedSessionBanner({
  reason,
  abortReason,
  canViewReport = true,
  sessionId,
  className = '',
}: AbortedSessionBannerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const r = (reason ?? abortReason ?? '') as string;

  // empty substate
  if (!r) return null;

  const phaseFail = r.startsWith('phase_fail:') ? r.slice('phase_fail:'.length) : null;
  const reasonCopy = phaseFail
    ? t('live.aborted.phase_fail', { phase: phaseFail, defaultValue: `Council failed during ${phaseFail} phase.` })
    : t(`live.aborted.${r}`, {
        defaultValue: t('live.aborted.unknown', {
          defaultValue: 'Council aborted — no specific reason captured.',
        }),
      });

  const showRefundCopy = !UNCERTAIN_REASONS.has(r);
  const refundCopy = showRefundCopy
    ? t('live.aborted.refund_note', {
        defaultValue: 'Treated as infrastructure failure · refund issued automatically.',
      })
    : null;

  const dataState = phaseFail ? 'error' : 'default';

  return (
    <section
      role="alert"
      className={`max-w-2xl mx-auto px-8 py-12 text-center ${className}`}
      data-testid="aborted-session-banner"
      data-state={dataState}
      data-abort-reason={r}
    >
      <svg
        viewBox="0 0 64 64"
        width="64"
        height="64"
        className="mx-auto mb-6 text-ink-3"
        aria-hidden="true"
      >
        <circle cx="32" cy="32" r="24" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <path d="M22 32 H42" stroke="currentColor" strokeWidth="1.5" />
      </svg>

      <Eyebrow tone="ash" className="mb-3">
        {t('empty.aborted.eyebrow', { defaultValue: 'Council aborted' })}
      </Eyebrow>
      <h2
        className="font-serif text-2xl font-medium mb-3"
        data-testid="aborted-session-headline"
        tabIndex={-1}
      >
        {t('live.aborted.title', {
          defaultValue: t('empty.aborted.title', { defaultValue: 'Council could not complete' }),
        })}
      </h2>
      <p className="text-ink-2 leading-[1.7] mb-3">{reasonCopy}</p>
      {refundCopy && <p className="text-sm text-ink-3 mb-3">{refundCopy}</p>}

      <p className="text-[10px] tracking-meta uppercase text-ink-3 font-mono mb-8">
        abort_reason: {r}
        {sessionId ? ` · session: ${sessionId.slice(0, 12)}…` : ''}
      </p>

      <div className="flex items-center justify-center gap-3">
        <Button
          variant="primary"
          onClick={() => navigate('/dashboard')}
          data-testid="aborted-session-retry-cta"
        >
          {t('empty.aborted.retryCta', { defaultValue: 'Start a new council' })}
        </Button>
        {canViewReport && sessionId && (
          <Link
            to={`/sessions/${sessionId}/report`}
            data-testid="aborted-session-view-report"
            className="text-sm underline text-azure-700"
          >
            {t('live.aborted.viewReport', { defaultValue: 'View partial output' })} →
          </Link>
        )}
      </div>
    </section>
  );
}
